/**
 * Inline approval card. Rendered inside a Luna assistant turn whenever
 * the agent proposes a file change. Shows a collapsed summary by
 * default (path + one-line reason + Apply/Reject) and expands to a
 * line-level diff when the user wants to see the full change.
 *
 * The diff algorithm is a simple Longest-Common-Subsequence → diff
 * transformation, enough for the typical file-size we operate on
 * (tens of KB). If a file is enormous we fall back to showing the new
 * content without a diff — still useful for review, but no
 * side-by-side.
 */

import { useMemo, useState } from 'react';
import { useLuna, type Proposal } from '../../state/luna-store';

export function ApprovalCard({ proposal }: { proposal: Proposal }) {
  const apply = useLuna((s) => s.applyProposal);
  const reject = useLuna((s) => s.rejectProposal);
  const [expanded, setExpanded] = useState(false);

  const diff = useMemo(
    () => computeDiff(proposal.oldContent || '', proposal.newContent || ''),
    [proposal.oldContent, proposal.newContent]
  );

  const isNew = proposal.oldContent == null || proposal.oldContent === '';
  const stats = diff.reduce(
    (acc, row) => {
      if (row.kind === 'add') acc.added++;
      else if (row.kind === 'del') acc.removed++;
      return acc;
    },
    { added: 0, removed: 0 }
  );

  return (
    <div
      className={
        'my-2 overflow-hidden rounded-lg border text-[12px] ' +
        (proposal.status === 'applied'
          ? 'border-green-500/40 bg-green-50'
          : proposal.status === 'rejected'
            ? 'border-border bg-surface-subdued opacity-70'
            : proposal.status === 'error'
              ? 'border-critical/40 bg-critical/5'
              : 'border-interactive/40 bg-interactive/[0.03]')
      }
    >
      <header className="flex items-start gap-2 px-3 py-2">
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-interactive/10 text-interactive">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <code className="truncate font-mono text-[11.5px] font-semibold text-text">
              {proposal.path}
            </code>
            {isNew && (
              <span className="rounded bg-green-500/15 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                novo
              </span>
            )}
            {!isNew && (
              <span className="text-[10px] text-text-subdued">
                <span className="text-green-700">+{stats.added}</span>{' '}
                <span className="text-red-700">-{stats.removed}</span>
              </span>
            )}
          </div>
          {proposal.reason && (
            <p className="mt-0.5 text-[12px] leading-snug text-text-subdued">
              {proposal.reason}
            </p>
          )}
        </div>

        {proposal.status === 'pending' && (
          <div className="flex shrink-0 gap-1">
            <button
              onClick={() => reject(proposal.proposalId)}
              className="rounded border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-text hover:bg-surface-hovered"
            >
              Descartar
            </button>
            <button
              onClick={() => void apply(proposal.proposalId)}
              className="rounded bg-interactive px-2.5 py-1 text-[11px] font-semibold text-white hover:brightness-110"
            >
              Aplicar
            </button>
          </div>
        )}
        {proposal.status === 'applying' && (
          <span className="text-[11px] text-text-subdued">Aplicando…</span>
        )}
        {proposal.status === 'applied' && (
          <span className="flex items-center gap-1 text-[11px] font-medium text-green-700">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            Aplicado
          </span>
        )}
        {proposal.status === 'rejected' && (
          <span className="text-[11px] text-text-subdued">Descartado</span>
        )}
        {proposal.status === 'error' && (
          <span className="text-[11px] text-critical" title={proposal.errorMessage}>
            Falha
          </span>
        )}
      </header>

      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1 border-t border-border/50 bg-surface/50 px-3 py-1.5 text-[11px] font-medium text-text-subdued hover:bg-surface"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{ transform: expanded ? 'rotate(90deg)' : undefined }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        {expanded ? 'Esconder diff' : 'Ver diff'}
      </button>

      {expanded && (
        <pre className="lc-scroll max-h-[320px] overflow-auto border-t border-border/50 bg-[#0b1220] px-3 py-2 font-mono text-[11px] leading-[1.45] text-slate-100">
          {diff.map((row, i) => (
            <div
              key={i}
              className={
                row.kind === 'add'
                  ? 'bg-green-500/15 text-green-200'
                  : row.kind === 'del'
                    ? 'bg-red-500/15 text-red-200'
                    : 'text-slate-400'
              }
            >
              <span className="mr-1.5 select-none opacity-60">
                {row.kind === 'add' ? '+' : row.kind === 'del' ? '-' : ' '}
              </span>
              {row.text || '\u00a0'}
            </div>
          ))}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Minimal line-level diff
// ---------------------------------------------------------------------------

type DiffRow = { kind: 'eq' | 'add' | 'del'; text: string };

/**
 * LCS-based line diff. Good enough for theme files (Liquid, JSON, CSS)
 * which rarely exceed a few hundred lines. For huge files we skip the
 * LCS and just emit "removed old + added new" so the UI still renders.
 */
function computeDiff(oldText: string, newText: string): DiffRow[] {
  const a = oldText.split('\n');
  const b = newText.split('\n');
  const MAX = 1500; // bail threshold
  if (a.length > MAX || b.length > MAX) {
    return [
      ...a.map((l) => ({ kind: 'del' as const, text: l })),
      ...b.map((l) => ({ kind: 'add' as const, text: l })),
    ];
  }
  const n = a.length;
  const m = b.length;
  // LCS DP (Int16Array keeps memory bounded on medium files)
  const dp = new Int16Array((n + 1) * (m + 1));
  const width = m + 1;
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) {
        dp[i * width + j] = dp[(i + 1) * width + (j + 1)] + 1;
      } else {
        const down = dp[(i + 1) * width + j];
        const right = dp[i * width + (j + 1)];
        dp[i * width + j] = down > right ? down : right;
      }
    }
  }
  const out: DiffRow[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'eq', text: a[i] });
      i++;
      j++;
    } else if (dp[(i + 1) * width + j] >= dp[i * width + (j + 1)]) {
      out.push({ kind: 'del', text: a[i] });
      i++;
    } else {
      out.push({ kind: 'add', text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ kind: 'del', text: a[i++] });
  while (j < m) out.push({ kind: 'add', text: b[j++] });
  return out;
}
